import { supabase } from "./supabase";
import type { Oekt } from "./session";
import { finnesAlt } from "@infoflyt/shared/lager";

/**
 * Dokumenter: opplasting fra nettflata.
 *
 * TO LAG, OG DET ER SKJEMAETS EGEN DELING. `document_series` er dokumentet
 * som SAK — «dagsplanen for opptaksdag 7» — og `document` er én opplastet
 * versjon av den. Serien opprettes én gang og lever hele produksjonen;
 * versjonene legger seg oppaa. Appen viser hoeyeste versjon, historikken
 * ligger her. Se docs/06-modul-planer.md.
 *
 * Selve fila ligger i Storage-boetta `dokumenter`, og raden peker paa den
 * med `file_path`. Boetta er privat: nedlasting gaar gjennom en policy, ikke
 * gjennom en gjettbar URL.
 */

export const BOETTE = "dokumenter";

/**
 * Dokumentslagene nettflata bruker. Speiler enumet `document_kind`.
 *
 * `annet` er for feltene prodleder legger til selv — en turnus, et kart.
 * Tittelen baerer meningen; slaget sier bare at den ikke er en av de kjente.
 * `klippemanus` finnes i enumet, men hoerer til redaktoerflata i fase 6.
 */
export type Slag =
  | "dagsmanus" | "dagsplan" | "kjoreplan" | "nedbrekk" | "kameraplan" | "annet";

export interface Versjon {
  readonly id: string;
  readonly versjon: number;
  readonly filsti: string;
  readonly filnavn: string;
  readonly lastetOpp: string;
  /** ISO-tidspunkt, eller null for «med én gang». */
  readonly visesFra: string | null;
}

export interface Serie {
  readonly id: string;
  readonly slag: Slag | null;
  readonly tittel: string;
  /** Hoeyeste versjon, eller null naar ingenting er lastet opp. */
  readonly siste: Versjon | null;
}

/**
 * Filnavnet ligger IKKE i databasen.
 *
 * `document` har `file_path`, ikke et visningsnavn. Navnet koordinatoren
 * kjenner igjen er siste ledd i stien, og det holder — stien bygges av oss og
 * beholder originalnavnet. Ett felt mindre aa holde i takt.
 */
function filnavnAv(sti: string): string {
  const siste = sti.slice(sti.lastIndexOf("/") + 1);
  /* Versjonsprefikset er vaart, ikke koordinatorens. Stien maa ha det for at
     to versjoner av samme fil ikke skal kollidere i boetta; navnet paa
     kortet skal vaere det hen dro inn. */
  return siste.replace(/^v\d+-/, "");
}

/**
 * Fjerner en opplastet versjon.
 *
 * ========  HVORFOR DETTE ER LOV, NAAR RAPPORTER IKKE KAN FJERNES  ========
 *
 * Brukeren 29.10.2026: «Dokumenter som lastes opp eksternt (ikke lagd i
 * vårt system) er lagret andre steder, så de trenger vi ikke være like
 * strenge med angående sletting.» En dagsplan kom fra en e-post og ligger
 * der fortsatt; en rapport LAGES her og finnes ikke noe annet sted.
 *
 * ========  RADEN FOERST, SAA FILA  ========
 *
 * Rekkefoelgen er ikke tilfeldig. Gaar fila foerst og raden ikke lar seg
 * slette, staar det et kort som peker paa en fil som ikke finnes — og den
 * som trykker faar en nedlasting som feiler uten aa vite hvorfor. Gaar
 * raden foerst, er verste utfall en foreldreloes PDF inne i produksjonens
 * egen mappe. Den koster plass, ikke tillit.
 *
 * ========  VERSJONEN, IKKE SERIEN  ========
 *
 * Er v3 feil, skal v2 bli gjeldende igjen — ikke hele dokumentet
 * forsvinne. Serien blir staaende tom naar siste versjon fjernes, og det
 * er riktig: feltet er en plass paa opplastingssida, ikke en fil.
 */
export async function fjernVersjon(
  dokumentId: string, filsti: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };

  const { error } = await supabase.from("document").delete().eq("id", dokumentId);
  if (error !== null) {
    return { ok: false, feil: `Fikk ikke fjernet dokumentet: ${error.message}` };
  }
  await supabase.storage.from(BOETTE).remove([filsti]);
  return { ok: true };
}

/** Seriene for produksjonen og opptaksdagen, med siste versjon paa hver. */
export async function hentSerier(oekt: Oekt): Promise<Serie[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("document_series")
    .select("id, kind, title, document(id, version, file_path, uploaded_at, visible_from)")
    .eq("production_id", oekt.production.id)
    .eq("shoot_day", oekt.shootDay);

  if (error !== null || data === null) return [];

  return data.map((rad) => {
    const versjoner = (rad.document ?? []) as {
      id: string; version: number; file_path: string;
      uploaded_at: string; visible_from: string | null;
    }[];
    /* Hoeyeste versjon, ikke nyeste `uploaded_at`. En rettelse lastet opp
       etter en nyere versjon skal ikke kunne gaa foran den. */
    const siste = versjoner.reduce<typeof versjoner[number] | null>(
      (best, v) => (best === null || v.version > best.version ? v : best), null);
    return {
      id: String(rad.id),
      slag: rad.kind as Slag | null,
      tittel: String(rad.title),
      siste: siste === null ? null : {
        id: siste.id,
        versjon: siste.version,
        filsti: siste.file_path,
        filnavn: filnavnAv(siste.file_path),
        lastetOpp: siste.uploaded_at,
        visesFra: siste.visible_from,
      },
    };
  });
}

/**
 * Finner serien for et slag, eller lager den.
 *
 * Seriene opprettes ikke paa forhaand. En produksjon som aldri laster opp en
 * kjoereplan skal ikke ha en tom rad for den — og koordinatoren skal ikke
 * maatte «opprette» noe foer hen kan laste opp.
 */
async function serieFor(
  oekt: Oekt, slag: Slag, tittel: string, dag: string,
): Promise<string | null> {
  if (supabase === null) return null;
  /*
   * Egendefinerte felter slaas opp paa TITTEL, de faste paa slag.
   *
   * Alle egendefinerte har slaget `annet`, saa to av dem ville truffet
   * hverandre hvis oppslaget gikk paa slag alene. For de faste er slaget det
   * riktige: tittelen kan endres uten at serien skifter identitet.
   */
  let sp = supabase
    .from("document_series")
    .select("id")
    .eq("production_id", oekt.production.id)
    .eq("shoot_day", dag)
    .eq("kind", slag);
  if (slag === "annet") sp = sp.eq("title", tittel);
  const { data: fins } = await sp.limit(1).maybeSingle();
  if (fins !== null) return String(fins.id);

  /*
   * ARVER EN GAMMEL «ANNET»-SERIE MED SAMME TITTEL.
   *
   * Et fast slag kan ha kommet til ETTER at noen lagde sitt eget felt med
   * samme navn. «Kameraplan» var et egendefinert felt foer det ble et slag.
   * Uten dette ville neste opplasting havnet i en ny, tom serie ved siden av
   * den som alt har filene — to bokser med samme navn, bare én med innhold.
   */
  if (slag !== "annet") {
    const { data: gammel } = await supabase
      .from("document_series")
      .select("id")
      .eq("production_id", oekt.production.id)
      .eq("shoot_day", dag)
      .eq("kind", "annet")
      .eq("title", tittel)
      .limit(1)
      .maybeSingle();
    if (gammel !== null) return String(gammel.id);
  }

  const { data, error } = await supabase
    .from("document_series")
    .insert({
      production_id: oekt.production.id,
      kind: slag,
      title: tittel,
      shoot_day: dag,
    })
    .select("id")
    .single();
  return error === null && data !== null ? String(data.id) : null;
}

export interface Opplasting {
  readonly ok: boolean;
  readonly feil?: string;
}

/**
 * Laster opp én versjon.
 *
 * Rekkefoelgen er FILA FOERST, raden etterpaa. Feiler opplastingen, finnes
 * det ingen rad som peker paa en fil som ikke er der — og appen ser aldri et
 * dokument den ikke kan hente. Motsatt rekkefoelge ville gitt et dokument som
 * finnes i lista og kaster naar noen trykker paa det.
 */
export async function lastOpp(
  oekt: Oekt,
  slag: Slag,
  tittel: string,
  fil: File,
  visesFra: string | null,
  /*
   * Opptaksdagen dokumentet hoerer til. Normalt den man staar paa.
   *
   * KAMERAPLANEN PUBLISERES TIL EN ANNEN DAG ENN DEN MAN STAAR PAA. A-foto
   * tegner i dag og publiserer til dag 9; arket skal da ligge paa dag 9, og
   * ikke paa dagen hen tegnet det.
   */
  dag: string = oekt.shootDay,
): Promise<Opplasting> {
  if (supabase === null) return { ok: false, feil: "Ingen forbindelse til databasen." };

  const serieId = await serieFor(oekt, slag, tittel, dag);
  if (serieId === null) return { ok: false, feil: "Fikk ikke opprettet dokumentserien." };

  // Neste versjonsnummer. `unique (series_id, version)` fanger et kappløp.
  const { data: siste } = await supabase
    .from("document")
    .select("version")
    .eq("series_id", serieId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  const versjon = (siste === null ? 0 : Number(siste.version)) + 1;

  const sti = `${oekt.production.id}/${serieId}/v${versjon}-${fil.name}`;
  /*
   * RAA BYTES, IKKE BLOB. Se apps/mobile/src/state/rapportsynk.ts.
   *
   * `storage.upload` med en Blob bygger en FormData og legger fila i et
   * felt UTEN NAVN — `body.append("", fil)` i StorageFileApi. Paa iOS kom
   * ikke den delen med, og tjeneren svarte «No content provided» paa en
   * opplasting som hadde en gyldig fil i haanden. En ArrayBuffer tar den
   * andre veien i samme klient: raa kropp med `content-type`, og ingen
   * multipart som kan miste noe.
   *
   * Dette virker fra maskin. Det staar her fordi flata ogsaa brukes paa
   * sett, og da kan det vaere en iPad.
   */
  const { error: filfeil } = await supabase.storage
    .from(BOETTE)
    .upload(sti, await fil.arrayBuffer(), { contentType: "application/pdf", upsert: false });
  /*
   * Moeter vi vaar egen forrige opplasting, er fila framme: svaret kan ha
   * gaatt tapt paa vei tilbake uten at fila gjorde det. Stien baerer
   * versjonen, og en gitt versjon har de samme bytesene — saa vi gaar
   * videre til raden i stedet for aa la koordinatoren staa fast paa en
   * fil som alt ligger der.
   */
  if (filfeil !== null && !finnesAlt(filfeil)) {
    return { ok: false, feil: `Fikk ikke lastet opp fila: ${filfeil.message}` };
  }

  const { error } = await supabase.from("document").insert({
    series_id: serieId,
    production_id: oekt.production.id,
    version: versjon,
    file_path: sti,
    visible_from: visesFra,
    uploaded_by: oekt.member.id,
  });

  if (error !== null) {
    /*
     * FILA BLIR LIGGENDE. Her sto det en `remove` som ryddet den bort
     * naar raden ikke ble til — og den var feil paa to maater.
     *
     * Den slettet automatisk, og det skal ingenting gjoere med et
     * dokument som er lastet opp paa nett. Og den gjorde et nytt forsoek
     * umulig aa feilsoeke: neste forsoek regner samme versjonsnummer ut
     * av `document`-radene, treffer samme sti, og med `upsert: false`
     * ville opplastingen blitt avvist. Nå leses det som «fila er framme»
     * og raden proeves paa nytt alene.
     *
     * Prisen er en foreldreloes fil hvis raden aldri blir til. Den ligger
     * inne i produksjonens egen mappe, bak de samme policyene som alt
     * annet, og faar foelge produksjonen naar den ryddes.
     */
    return { ok: false, feil: `Fikk ikke lagret dokumentet: ${error.message}` };
  }
  return { ok: true };
}

/**
 * ⚠ IKKE I BRUK LENGER, og det er med vilje.
 *
 * `visible_from` skjulte dokumentet til et klokkeslett satt for haand.
 * Appen leser ikke feltet lenger: et dokument er lesbart fra sekundet
 * det lastes opp, og PLASSERINGEN foelger opptaksdagen — se CLAUDE.md,
 * avgjort 02.11.2026. Kolonnen staar igjen i basen, og funksjonen her,
 * til det er avklart om noe annet skal bruke den.
 */
/**
 * Setter naar en versjon blir synlig i appen.
 *
 * Klokkeslettet gjelder I DAG. Er tidspunktet alt passert, blir dokumentet
 * synlig med én gang — det er riktigere enn aa gjette at koordinatoren mente
 * i morgen.
 */
export async function settVisesFra(dokumentId: string, klokkeslett: string): Promise<void> {
  if (supabase === null) return;
  let naar: string | null = null;
  if (klokkeslett !== "") {
    const [t, m] = klokkeslett.split(":").map(Number);
    const d = new Date();
    d.setHours(t ?? 0, m ?? 0, 0, 0);
    naar = d.toISOString();
  }
  await supabase.from("document").update({ visible_from: naar }).eq("id", dokumentId);
}
